import { useEffect, useRef, useState } from "react";
import { PaperPlaneRight, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, streamPayChat } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export default function PayChatPanel({ employee, onApplied }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get(`/ai/pay-chat/${employee.id}/history`).then((r) => {
      setMessages(r.data.map((m) => ({ role: m.role, content: m.content })));
      const last = r.data[r.data.length - 1];
      if (last) setSessionId(last.session_id);
    }).catch(() => {});
  }, [employee.id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    await streamPayChat({
      employeeId: employee.id,
      message: text,
      sessionId,
      onSession: (sid) => setSessionId(sid),
      onDelta: (chunk) =>
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
          return copy;
        }),
      onDone: (ev) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: ev.clean_text, applied: ev.applied_updates };
          return copy;
        });
        if (ev.applied_updates) {
          toast.success("Pay parameters updated — logged to audit trail");
          onApplied?.();
        }
        setStreaming(false);
      },
      onError: (msg) => {
        toast.error(msg || "AI request failed");
        setStreaming(false);
      },
    });
  };

  return (
    <div className="flex flex-col h-[480px] bg-card border border-border rounded-md" data-testid="pay-chat-panel">
      <div className="px-5 py-3.5 border-b border-border flex items-center gap-2">
        <Sparkle size={17} weight="duotone" className="text-indigo-600" />
        <p className="text-sm font-semibold font-heading tracking-tight">AI pay setup — {employee.full_name}</p>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.length === 0 && (
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Describe how this team member should be paid. Examples:</p>
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li>"Set Maya to $26/hr, biweekly, 40h weeks"</li>
              <li>"Give a 5% raise effective now"</li>
              <li>"Switch to salary at $95k paid monthly"</li>
            </ul>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-md px-4 py-2.5 text-sm whitespace-pre-wrap ${
              m.role === "user" ? "bg-[#0A0F1C] text-white" : "bg-muted text-foreground"}`}>
              {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
              {m.applied && (
                <div className="mt-2 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="ai-applied-badge">
                  ✓ Applied: {Object.entries(m.applied).map(([k, v]) => `${k} → ${v}`).join(", ")}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-border p-3 flex gap-2">
        <Input
          data-testid="pay-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="e.g. Raise hourly rate to $27.50…"
          disabled={streaming}
        />
        <Button data-testid="pay-chat-send-btn" onClick={send} disabled={streaming || !input.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0">
          <PaperPlaneRight size={17} />
        </Button>
      </div>
    </div>
  );
}
